import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';

import type { WebWidgetModel } from '@web/types';

import { TranslateModule } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { map, Subject, take, type Observable } from 'rxjs';

import { AppFacade } from '@web/store/app/app.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { UserFacade } from '@web/store/user/user.facade';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';

import { ToolbarTabComponent } from '@web/shared/components/web-toolbar-tab/web-toolbar-tab.component';
import { ToolbarWidgetComponent } from '@web/shared/components/web-toolbar-widget/web-toolbar-widget.component';
import { ToolbarGridComponent } from '@web/shared/components/web-toolbar-grid/web-toolbar-grid.component';
import { WebWidgetGroupPipe } from '@web/shared/pipes/widget-group.pipe';

type OnClosePayload = WidgetOnClosePayload | GridOnClosePayload | TabOnClosePayload;

type WidgetOnClosePayload = {
    type: 'widget';
    entity: WebWidgetModel;
};

type GridOnClosePayload = {
    type: 'grid';
    variant: number;
};

type TabOnClosePayload = {
    type: 'tab';
    variant: number;
};

@Component({
    selector: 'web-add-widget-or-grid',
    templateUrl: './add-widget-or-grid.component.html',
    styleUrls: ['./add-widget-or-grid.component.less'],
    standalone: true,
    imports: [
        AsyncPipe,
        TranslateModule,
        ToolbarWidgetComponent,
        ToolbarGridComponent,
        ToolbarTabComponent,
        WebWidgetGroupPipe,
    ],
})
export class AddWidgetOrGridComponent {
    @Input() public isSpecific: boolean = false;
    @Input() public isDisabledLogoWidget: boolean;
    @Output() public addWidget: EventEmitter<WebWidgetModel> = new EventEmitter<WebWidgetModel>();

    private readonly bsModalRef: BsModalRef = inject(BsModalRef);
    private readonly appFacade: AppFacade = inject(AppFacade);
    private readonly userFacade: UserFacade = inject(UserFacade);
    private readonly pagesFacade: PagesFacade = inject(PagesFacade);
    private readonly widgetsService: WebWidgetsService = inject(WebWidgetsService);

    public readonly isAdmin$: Observable<boolean> = this.appFacade.isAdmin$;
    public readonly isEduSite$: Observable<boolean> = this.pagesFacade.isEduSite$;
    public readonly eduUserTariff$: Observable<string> = this.userFacade.eduUserTariff$;

    public currentTab: 'widget' | 'grid' = 'widget';

    public onClose: Subject<OnClosePayload> = new Subject<OnClosePayload>();

    public isWidgetDisabled(widget: WebWidgetModel) {
        return this.isAdmin$.pipe(
            take(1),
            map((isAdmin) => {
                return !isAdmin && (widget.params.disabled || !widget.permission.isReleased);
            })
        );
    }

    public onAddWidget(widget: WebWidgetModel): void {
        this.isWidgetDisabled(widget).subscribe((disabled) => {
            if (disabled) {
                return;
            }

            this.onClose.next({ type: 'widget', entity: widget });
            this.bsModalRef.hide();
        });
    }

    public onAddGrid(variant: number): void {
        this.onClose.next({ type: 'grid', variant });
        this.bsModalRef.hide();
    }

    public onAddTab(variant: number): void {
        this.onClose.next({ type: 'tab', variant });
        this.bsModalRef.hide();
    }

    public get widgets$(): Observable<WebWidgetModel[]> {
        if (this.isSpecific) {
            return this.widgetsService.getSpecificWidgets$();
        }

        return this.widgetsService.getVisibleWidgets$();
    }

    public onCancel(): void {
        this.onClose.next(null);
        this.bsModalRef.hide();
    }
}
